import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Popover } from "@/components/ui/Popover";
import { Icon } from "@/components/ui/Icon";
import { tagColor, SCALE } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { ViewColumn, ViewStatusOption } from "@/lib/boardView";

interface TagOption {
  id: string;
  label: string;
  color: string;
}

interface TagsCellProps {
  value: string[];
  rowH?: number;
  onClick?: () => void;
  options?: ViewStatusOption[] | TagOption[];
  column?: ViewColumn;
  onPatch?: (next: string[]) => void;
  onAddOption?: (label: string) => Promise<TagOption | null>;
  canCreateOption?: boolean;
}

const CHIP_BASE = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  padding: "2px 8px",
  fontSize: 11.5,
  color: SCALE[700],
  borderRadius: 4,
  fontWeight: 500 as const,
  whiteSpace: "nowrap" as const,
  flexShrink: 0,
};

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 640px)").matches;
}

function resolveChipColor(label: string, options?: TagsCellProps["options"]): string {
  const opt = options?.find((o) => o.label === label || o.id === label);
  if (
    opt &&
    typeof (opt as TagOption).color === "string" &&
    (opt as TagOption).color.startsWith("#")
  ) {
    return (opt as TagOption).color;
  }
  return tagColor(label);
}

export function TagsCell({
  value,
  rowH = 44,
  onClick,
  options,
  onPatch,
  onAddOption,
  canCreateOption = false,
}: TagsCellProps) {
  const tags = value || [];
  const editable = typeof onPatch === "function";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [mobile, setMobile] = useState<boolean>(() => isMobileViewport());
  const [creating, setCreating] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onResize = () => setMobile(isMobileViewport());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 16);
      return () => window.clearTimeout(id);
    }
    setQuery("");
    setHighlight(0);
    return undefined;
  }, [open]);

  const selectedSet = useMemo(() => new Set(tags), [tags]);
  const allOptions: TagOption[] = useMemo(() => {
    const fromOptions: TagOption[] = (options ?? []).map((o) => ({
      id: o.id,
      label: o.label,
      color: typeof (o as TagOption).color === "string" ? (o as TagOption).color : "gray",
    }));
    const known = new Set(fromOptions.map((o) => o.label));
    const orphans: TagOption[] = tags
      .filter((tag) => !known.has(tag))
      .map((tag) => ({ id: tag, label: tag, color: "gray" }));
    return [...fromOptions, ...orphans];
  }, [options, tags]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [allOptions, query]);

  const canMint =
    Boolean(canCreateOption) &&
    typeof onAddOption === "function" &&
    query.trim().length > 0 &&
    !allOptions.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  const rowCount = filtered.length + (canMint ? 1 : 0);

  useEffect(() => {
    if (highlight >= rowCount) setHighlight(Math.max(0, rowCount - 1));
  }, [rowCount, highlight]);

  const handleOpen = () => {
    if (!editable) {
      onClick?.();
      return;
    }
    setOpen(true);
  };

  const toggleTag = (label: string) => {
    if (!onPatch) return;
    const next = selectedSet.has(label) ? tags.filter((tg) => tg !== label) : [...tags, label];
    onPatch(next);
  };

  const mintAndSelect = async () => {
    if (!onAddOption) return;
    const label = query.trim();
    if (!label) return;
    setCreating(true);
    try {
      const created = await onAddOption(label);
      if (created && onPatch) {
        if (!selectedSet.has(created.label)) {
          onPatch([...tags, created.label]);
        }
        setQuery("");
        setHighlight(0);
      }
    } finally {
      setCreating(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (rowCount === 0) return;
      setHighlight((h) => (h + 1) % rowCount);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (rowCount === 0) return;
      setHighlight((h) => (h - 1 + rowCount) % rowCount);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rowCount === 0) return;
      if (highlight < filtered.length) {
        const opt = filtered[highlight];
        if (opt) toggleTag(opt.label);
      } else if (canMint) {
        void mintAndSelect();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Backspace" && query.length === 0 && tags.length > 0) {
      e.preventDefault();
      const next = tags.slice(0, -1);
      onPatch?.(next);
    }
  };

  const displayBody =
    tags.length === 0 ? (
      <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>+ {t.tags}</span>
    ) : (
      <div style={{ display: "flex", gap: 4, alignItems: "center", overflow: "hidden" }}>
        {tags.slice(0, 3).map((tag) => (
          <span key={tag} style={{ ...CHIP_BASE, background: resolveChipColor(tag, options) }}>
            {tag}
          </span>
        ))}
        {tags.length > 3 ? (
          <span style={{ fontSize: 11, color: "var(--ink-3)", marginLeft: 2 }}>
            +{tags.length - 3}
          </span>
        ) : null}
      </div>
    );

  const renderPicker = () => (
    <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          name="tag-search"
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t.tags}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 10px",
            fontSize: 13,
            border: "1px solid var(--hairline-strong)",
            borderRadius: 6,
            background: "var(--surface)",
            color: "var(--ink-1)",
            outline: "none",
            fontFamily: "inherit",
          }}
        />
      </div>

      {tags.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            padding: "0 2px",
          }}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              style={{ ...CHIP_BASE, background: resolveChipColor(tag, options), gap: 4 }}
            >
              {tag}
              <button
                onClick={() => toggleTag(tag)}
                aria-label={`${tag} entfernen`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  background: "transparent",
                  color: SCALE[700],
                  cursor: "pointer",
                  padding: 0,
                  marginLeft: 2,
                  fontSize: 12,
                  lineHeight: 1,
                  opacity: 0.7,
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.opacity = "1")}
                onMouseLeave={(ev) => (ev.currentTarget.style.opacity = "0.7")}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div
        style={{
          maxHeight: mobile ? "50vh" : 240,
          overflowY: "auto",
          margin: "0 -4px",
          padding: "0 4px",
        }}
      >
        {filtered.length === 0 && !canMint ? (
          <div
            style={{
              padding: "10px 8px",
              fontSize: 12,
              color: "var(--ink-4)",
              textAlign: "center",
            }}
          >
            {query ? "Keine Treffer" : "Keine Optionen"}
          </div>
        ) : null}

        {filtered.map((opt, idx) => {
          const selected = selectedSet.has(opt.label);
          const highlighted = idx === highlight;
          return (
            <button
              key={opt.id}
              onClick={() => toggleTag(opt.label)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                width: "100%",
                border: "none",
                background: highlighted ? "var(--surface-soft)" : "transparent",
                borderRadius: 6,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span style={{ ...CHIP_BASE, background: resolveChipColor(opt.label, options) }}>
                {opt.label}
              </span>
              {selected ? (
                <Icon name="check" size={14} color="var(--accent)" style={{ marginLeft: "auto" }} />
              ) : null}
            </button>
          );
        })}

        {canMint
          ? (() => {
              const idx = filtered.length;
              const highlighted = idx === highlight;
              return (
                <button
                  onClick={() => void mintAndSelect()}
                  onMouseEnter={() => setHighlight(idx)}
                  disabled={creating}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px",
                    width: "100%",
                    border: "none",
                    background: highlighted ? "var(--surface-soft)" : "transparent",
                    borderRadius: 6,
                    cursor: creating ? "wait" : "pointer",
                    textAlign: "left",
                    fontSize: 12.5,
                    color: "var(--ink-2)",
                    marginTop: 4,
                    borderTop: "1px solid var(--hairline)",
                  }}
                >
                  <Icon name="plus" size={14} />
                  <span>
                    „{query.trim()}" {creating ? "wird erstellt…" : "erstellen"}
                  </span>
                </button>
              );
            })()
          : null}
      </div>
    </div>
  );

  return (
    <>
      <CellWrap
        rowH={rowH}
        onClick={editable ? handleOpen : onClick ? handleOpen : undefined}
        interactive={editable || Boolean(onClick)}
        pad={tags.length === 0 ? "0 12px" : "0 10px"}
      >
        <div ref={anchorRef} style={{ width: "100%" }}>
          {displayBody}
        </div>
      </CellWrap>

      {open && editable ? (
        mobile ? (
          <MobileSheet onClose={() => setOpen(false)}>{renderPicker()}</MobileSheet>
        ) : (
          <Popover anchor={anchorRef.current} onClose={() => setOpen(false)} width={280}>
            {renderPicker()}
          </Popover>
        )
      ) : null}
    </>
  );
}

function MobileSheet({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13, 62, 99, 0.25)",
        zIndex: 500,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <div
        ref={ref}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "var(--surface)",
          color: "var(--ink-1)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          boxShadow: "0 -8px 28px rgba(13,62,99,0.18)",
          padding: 8,
          paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
          maxHeight: "85vh",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}
